import styled from 'styled-components';
import Image from 'next/image';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { ScrollableContent } from 'src/components/ScrollableContent';
import { Close } from 'src/components/icons/Close';
import { Button } from 'src/components/common/Button';
import { media } from 'src/styles/designs';
import { DirectusImage } from 'src/components/common/DirectusImage';
import { People } from 'src/graphql/schema.graphql';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';

export interface EventPageSpeakerModalProps {
  speaker: People;
  close: () => void;
}

const Container = styled.aside`
  position: absolute;
  top: 0;
  left: calc(var(--scrollbar-padding) * -1);
  right: 0;
  bottom: 0;
`;

const StyledScrollableContent = styled(ScrollableContent)`
  width: 100%;
  height: 100%;
  .simplebar-content-wrapper {
    display: flex;
    flex-direction: column;
  }
  .simplebar-content {
    flex: 1;
  }
`;

const ContentContainer = styled(ConstraintContainer)`
  max-width: 1020px;
  padding-top: 60px;
  padding-bottom: 60px;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 50px;
  ${media.w.lessThan.desktop} {
    padding-bottom: 30px;
    gap: 20px;
    align-items: flex-start;
    flex-direction: column;
    justify-content: center;
    height: calc(100% - 60px);
  }
`;

const CloseModalButton = styled(Button)`
  && {
    position: absolute;
    right: 10vw;
    top: 60px;
    padding: 8px;
    ${media.w.lessThan.desktop} {
      top: 20px;
      right: 20px;
      svg {
        width: 25px;
        height: 25px;
      }
    }
  }
`;

const SpeakerImageWrapper = styled.div`
  position: relative;
  min-width: 262px;
  height: 420px;
  background: ${({ theme }) => theme.text.palette.accent};
  ${media.w.lessThan.desktop} {
    min-width: 178px;
    height: 286px;
  }
`;

const SpeakerImage = styled(Image)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;

  ${media.w.greaterThanOrEqual.desktop} {
    object-position: center center;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 23px;
  ${media.w.lessThan.desktop} {
    gap: 8px;
  }
`;

const Heading = styled.h3`
  font-size: 21px;
  line-height: 26px;
  font-weight: 400;
  color: ${({ theme }) => theme.text.palette.accent};
  ${media.w.greaterThanOrEqual.desktop} {
    font-size: 26px;
    line-height: 30px;
  }
`;

const Description = styled.p`
  font-size: 14px;
  line-height: 18px;
  font-weight: 300;
  color: ${({ theme }) => theme.text.palette.primary};
  ${media.w.greaterThanOrEqual.desktop} {
    font-size: 16px;
    line-height: 24px;
  }
`;

export const EventPageSpeakerModal = ({
  speaker,
  close,
}: EventPageSpeakerModalProps) => {
  const translatedSpeaker = useTranslateItem(speaker);

  return (
    <Container>
      <StyledScrollableContent>
        <CloseModalButton
          kind="text"
          icon={Close}
          iconProps={{ size: 32, enabled: true }}
          onClick={close}
        />
        <ContentContainer>
          <SpeakerImageWrapper>
            {translatedSpeaker.portrait && (
              <SpeakerImage
                as={DirectusImage}
                src={translatedSpeaker.portrait.id}
                layout="fill"
              />
            )}
          </SpeakerImageWrapper>
          <Content>
            <Heading>{translatedSpeaker.name}</Heading>
            <Description>{translatedSpeaker.description}</Description>
          </Content>
        </ContentContainer>
      </StyledScrollableContent>
    </Container>
  );
};
